import { Benchmark, WorkflowItem } from '@cibo/core'
import { AxiosResponse } from 'axios'
import { createContext, useContext } from 'react'

export type FieldOverlapsModalState = {
  open: boolean
  workflowId?: string
  onOverlapsRemoved?: () => void
}
export type LedgerConflictsModalState = {
  open: boolean
  workflowId?: string
  onResolved?: () => void
  context?: 'enroll' | 'commit'
}
export type EnrollmentWindowClosedModalState = {
  workflowId?: string
  open: boolean
  programName?: string
  count?: number
  onDeleteSuccess?: () => void
  programId?: string
}

export type RemoveFieldsModalState = {
  workflowId: string
  fieldsToRemove: string[]
}

export type AddFieldsModalState = {
  workflowId: string
  onSuccess?: () => void
}

export type ClosedNameDialogState = { open: false }
export type CreateNameDialogState = {
  open: true
  variant: 'create'
  programId: string
  benchmark: Benchmark
  fieldIds: string[]
  onSuccess?: () => void
}
export type EditNameDialogState = {
  open: true
  variant: 'edit'
  currentName?: string
  workflowId: string
  onSuccess?: () => void
}
export type NameDialogState = ClosedNameDialogState | CreateNameDialogState | EditNameDialogState

export interface MessageUserDialogState {
  open: boolean
  workflow?: WorkflowItem
  navigateCallback?: (programId?: string) => void
}

export type DeleteWorkflowModalState = {
  workflowId: string
  onSuccess(): void
  onUndo?(arg: AxiosResponse<WorkflowItem>): void
}

export interface WorkflowManagementState {
  setRemoveFieldsModal(state: RemoveFieldsModalState): void
  setAddFieldsModal(state: AddFieldsModalState): void
  setNameDialog(state: NameDialogState): void
  setMessageUserDialog(state: MessageUserDialogState): void
  setDeleteWorkflowModal(state: DeleteWorkflowModalState): void
  setOverlapWarning(state: FieldOverlapsModalState): void
  setLedgerConflicts(state: LedgerConflictsModalState): void
  setEnrollmentWindowClosed(state: EnrollmentWindowClosedModalState): void
}

export const WorkflowManagementContext = createContext<WorkflowManagementState>({
  setRemoveFieldsModal: (state: RemoveFieldsModalState) => {
    throw new Error('removeFields context not provided')
  },
  setAddFieldsModal: (state: AddFieldsModalState) => {
    throw new Error('setAddFieldsModal context not provided')
  },
  setDeleteWorkflowModal: (state: DeleteWorkflowModalState) => {
    throw new Error('setDeleteWorkflowModal context not provided')
  },
  setNameDialog: (state: NameDialogState) => {
    throw new Error('setNameDialog context not provided')
  },
  setMessageUserDialog: (state: MessageUserDialogState) => {
    throw new Error('setMessageUserDialog context not provided')
  },
  setOverlapWarning: (state: FieldOverlapsModalState) => {
    throw new Error('setOverlapWarning context not provided')
  },
  setLedgerConflicts: (state: LedgerConflictsModalState) => {
    throw new Error('setLedgerConflicts context not provided')
  },
  setEnrollmentWindowClosed: (state: EnrollmentWindowClosedModalState) => {
    throw new Error('setEnrollmentWindowClosed context not provided')
  },
})
WorkflowManagementContext.displayName = 'WorkflowManagementContext'

export const useWorkflowManagement = () => useContext(WorkflowManagementContext)
